import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getUserAccessInfo, onAuthStateChange } from "@/integrations/firebase/auth";

const Index = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let hasNavigated = false;
    
    const safeNavigate = (path: string) => {
      if (!hasNavigated) {
        hasNavigated = true;
        clearTimeout(timeoutId);
        console.log(`🧭 Navigating to: ${path}`);
        navigate(path, { replace: true });
      }
    };
    
    const checkAuth = async (user: any) => {
      try {
        console.log('🔍 Checking auth state...');
        // Use the passed user (from native auth) or fall back to JS SDK's auth.currentUser
        const currentUser = user || auth.currentUser;
        console.log('👤 Current user:', currentUser ? `Logged in (${currentUser.uid})` : 'Not logged in');
        
        if (currentUser) {
          // Check if user has a client document (they're a client)
          console.log('📄 Checking client document...');
          try {
            const clientDoc = await getDoc(doc(db, "clients", currentUser.uid));
            if (clientDoc.exists()) {
              safeNavigate("/client-portal");
              return;
            }
          } catch (firestoreError: any) {
            console.warn('⚠️ Firestore client check error:', firestoreError.message);
            // If Firestore fails with permission denied, user might be a fresh OAuth sign-in
            // Try to check if they need to complete their profile
            if (firestoreError.code === 'permission-denied' || firestoreError.message?.includes('permission')) {
              console.log('🔄 Permission denied - checking if new OAuth user...');
              // For new OAuth users, redirect to complete-profile
              // They can't access Firestore yet because their profile doesn't exist
              try {
                const userDoc = await getDoc(doc(db, "users", currentUser.uid));
                if (!userDoc.exists()) {
                  console.log('📝 No user document - new OAuth user, redirecting to complete-profile');
                  safeNavigate("/complete-profile");
                  return;
                }
                const userData = userDoc.data();
                if (userData?.profileCompleted === false || userData?.tradeType === 'pending') {
                  console.log('📝 Profile incomplete, redirecting to complete-profile');
                  safeNavigate("/complete-profile");
                  return;
                }
                // Profile exists and is complete, go to dashboard
                safeNavigate("/dashboard");
              } catch (userDocError) {
                // Can't read user doc either - likely new OAuth user
                console.log('📝 Cannot read user doc - new OAuth user, redirecting to complete-profile');
                safeNavigate("/complete-profile");
              }
              return;
            }
          }
          
          // Get comprehensive user access info (handles Personal accounts automatically)
          console.log('🔐 Getting user access info...');
          try {
            const accessInfo = await getUserAccessInfo(currentUser.uid);
            console.log('📊 Access info:', accessInfo);
            
            // Check if user needs to complete their profile (OAuth users)
            const userDoc = await getDoc(doc(db, "users", currentUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.profileCompleted === false || userData.tradeType === 'pending') {
                console.log('📝 Profile incomplete, redirecting to complete-profile');
                safeNavigate("/complete-profile");
                return;
              }
            }
            
            // If user ONLY has client role, redirect to client portal
            if (accessInfo.roles.includes("client") && !accessInfo.isAdmin && !accessInfo.isManager && !accessInfo.isCrewLeader && !accessInfo.isEmployee) {
              safeNavigate("/client-portal");
              return;
            }
            
            // Check if user has contractor roles OR is a personal account
            const hasContractorRole = accessInfo.roles.length > 0 && accessInfo.roles.some(r => r !== 'client');
            
            if (hasContractorRole || accessInfo.isPersonalAccount) {
              // Contractor or Personal account - redirect to dashboard
              safeNavigate("/dashboard");
            } else if (accessInfo.roles.length === 0) {
              // User with no roles - could be new user, send to dashboard
              // The dashboard will handle showing appropriate content
              console.log('📝 User has no roles, sending to dashboard (new user)');
              safeNavigate("/dashboard");
            } else {
              // Has only client role - shouldn't reach here, but handle it
              safeNavigate("/client-portal");
            }
          } catch (accessError: any) {
            console.warn('⚠️ Access info check error:', accessError.message);
            // Default to dashboard if we can't check access
            safeNavigate("/dashboard");
          }
        } else {
          console.log('➡️ No user, navigating to /auth');
          safeNavigate("/auth");
        }
      } catch (error) {
        console.error('❌ Error in checkAuth:', error);
        // On error, still try to navigate to dashboard if we have a user
        if (user) {
          console.log('⚠️ Error occurred but user exists, going to dashboard');
          safeNavigate("/dashboard");
        } else {
          safeNavigate("/auth");
        }
      }
    };

    // Auth timeout for web app
    const timeoutDuration = 5000;

    console.log(`⏳ Setting auth timeout for ${timeoutDuration}ms`);
    
    timeoutId = setTimeout(() => {
      console.warn('⏰ Auth check timeout, redirecting to /auth');
      console.log('📊 Auth state at timeout:', {
        currentUser: auth.currentUser ? 'exists' : 'null',
      });
      safeNavigate("/auth");
    }, timeoutDuration);

    const initAuth = async () => {
      // On web, deep links are handled directly by the router via URL
      // The JS SDK persists auth state, so just set up the listener
      console.log('🔔 Setting up onAuthStateChanged listener (JS SDK)...');
      const unsubscribe = onAuthStateChange((user) => {
        console.log('🔔 onAuthStateChanged fired:', user ? `User present: ${user.uid}` : 'No user');
        clearTimeout(timeoutId);
        
        if (user) {
          checkAuth(user);
        } else {
          safeNavigate("/auth");
        }
      });
      
      console.log('✅ onAuthStateChanged listener registered');
      
      return unsubscribe;
    };

    let unsubscribePromise = initAuth();

    return () => {
      unsubscribePromise.then(unsub => unsub());
      clearTimeout(timeoutId);
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
};

export default Index;
